/**
 * Playback scrubber with elapsed/total readout.
 *
 * SIMULATED — the position comes from a timer, not from audio. The bar is not
 * interactive: there is nothing to seek here (the seekable control is SeekBar, which the
 * transport and the full-screen player use). Styles are the prototype's, with one
 * change: the transitions that make the tick look continuous are one tick long rather
 * than a hard-coded second, so they stay in step when the rate changes. See lib/playback.
 */
import SeekBar from './SeekBar';
import { formatTime, parseLength } from '../../lib/formatting';
import { c } from '../../styles/theme';

export interface PlayerBarProps {
  /** Seconds elapsed. */
  elapsedSec: number;
  /** Seconds buffered from the start of the media, when known. */
  bufferedSec?: number;
  /** Total length as a display string, e.g. "3:24". */
  lengthStr: string;
  /** Called when the user scrubs/seeks through the bar. */
  onSeek?: (seconds: number) => void;
  /** True while playback is running, which is the only time the shared seek bar animates. */
  playing?: boolean;
  /**
   * Playback rate, so the smoothing matches the tick.
   *
   * Defaults to 1×, which is the `1s linear` this bar always had — a caller that does
   * not thread speed through renders exactly as before. Passing it keeps the fill level
   * with the clock underneath it at 1.5× and 2×, where the tick is shorter than a second.
   */
  speed?: number;
}

export default function PlayerBar({
  elapsedSec,
  bufferedSec = 0,
  lengthStr,
  onSeek,
  playing = false,
  speed = 1,
}: PlayerBarProps) {
  const totalSec = parseLength(lengthStr);

  return (
    <div>
      <SeekBar
        value={elapsedSec}
        bufferedSec={bufferedSec}
        duration={totalSec}
        onSeek={onSeek ?? (() => undefined)}
        playing={playing}
        speed={speed}
        label="Seek within playback"
        style={{ marginBottom: 6 }}
      />
      {/* The position and the length: the only numbers on the bar, and the reason
          someone looks at it. text2 — text3 is 3.09:1 on surface1, and at 10px this is
          the smallest text in the component, which is where the fade hurt most. */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, color: c.text2, fontFamily: 'IBM Plex Mono' }}>
          {formatTime(elapsedSec)}
        </span>
        <span style={{ fontSize: 10, color: c.text2, fontFamily: 'IBM Plex Mono' }}>
          {lengthStr}
        </span>
      </div>
    </div>
  );
}
